import { RefObject, useEffect } from "react";

type OnScreenCallback = (isOnScreen: boolean) => void;

const callbackMap = new Map<Element, Array<OnScreenCallback>>();

const onScreenObserver = new IntersectionObserver(
  ([entry]) => {
    if (!callbackMap.has(entry.target)) return;
    const callbacks = callbackMap.get(entry.target)!;
    callbacks.forEach((callback) => callback(entry.intersectionRatio !== 0));
  },
  { root: null, threshold: 0 }
);

export const useIsOnScreenEffect = (
  elementSource: Element | RefObject<Element>,
  callback: OnScreenCallback,
  deps: ReadonlyArray<unknown>
) => {
  useEffect(() => {
    const element =
      elementSource instanceof Element ? elementSource : elementSource.current;
    if (!element) return;
    const callbacks = callbackMap.has(element)
      ? [...callbackMap.get(element)!, callback]
      : [callback];
    callbackMap.set(element, callbacks);
    onScreenObserver.observe(element);

    return () => {
      const callbacks = callbackMap
        .get(element)
        ?.filter((cb) => cb !== callback);
      if (!callbacks) return;
      if (callbacks.length === 0) {
        onScreenObserver.unobserve(element);
        callbackMap.delete(element);
      } else {
        callbackMap.set(element, callbacks);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementSource, ...deps]);
};
